import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  AdminCatalogOption,
  AdminConsignorOption,
  AdminProductSearchItem,
  ProductConditionOption,
  ProductSizeOption,
} from '../types/adminInventoryAdd';

/**
 * Admin-only endpoints for “Agregar inventario” (batch).
 *
 * ---
 * ### GET `/api/admin/consignors`
 * Lista consignadores para el selector. Incluir `id`, `fullName`, `email` (opcional), `commission` (%).
 *
 * ### GET `/api/admin/products/search?q=`
 * Búsqueda de productos (autocomplete). Respuesta: `{ data: { items: [...] } }` con `id`, `title`,
 * `brand`, `category`, y opcionalmente `thumbnailUrl` (string).
 *
 * ### GET `/api/admin/products/:productId/conditions`
 * Todas las condiciones disponibles para ese producto (tras seleccionarlo). `{ data: { items: [{ id, label }] } }`
 *
 * ### GET `/api/admin/products/:productId/sizes`
 * Todas las tallas para ese producto. `{ data: { items: [{ id, label }] } }`
 *
 * ### POST `/api/admin/inventory/batch`
 * Crea el lote. Body: `consignorId`, `rows: [{ productId, sizeId, conditionId, cost, publicPrice, comments }]`
 * (ajustar nombres al modelo real).
 *
 * ---
 * ### Catálogos rápidos para alta de producto en modal
 *
 * - `GET /api/admin/product-brands`
 * - `POST /api/admin/product-brands` body: `{ name }`
 * - `GET /api/admin/product-categories`
 * - `POST /api/admin/product-categories` body: `{ name }`
 * - `GET /api/admin/product-models/search?brandId=&categoryId=&q=`
 * - `POST /api/admin/product-models` body: `{ name, brandId, categoryId }`
 * - `POST /api/admin/products` (multipart/form-data):
 *   - `brandId`, `categoryId`, `modelId`, `colorway`, `sku`, `description`, `image`
 *
 * ---
 * ### Alta rápida de talla / condición
 *
 * - `POST /api/admin/product-sizes` body: `{ name, categoryId }`
 * - `POST /api/admin/product-conditions` body: `{ name, categoryId }`
 */

function unwrapItems<T>(body: unknown): T[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const { items } = inner;
  return Array.isArray(items) ? (items as T[]) : [];
}

function parseConsignor(raw: unknown): AdminConsignorOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  const commission = Number(r.commission);
  if (!Number.isFinite(id) || !Number.isFinite(commission)) return null;
  return {
    id,
    fullName: String(r.fullName ?? r.name ?? ''),
    email: r.email != null ? String(r.email) : null,
    commission,
  };
}

function parseProduct(raw: unknown): AdminProductSearchItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const tu = r.thumbnailUrl ?? r.imageUrl ?? r.image ?? r.photoUrl;
  const thumbnailUrl = typeof tu === 'string' && tu.trim() ? tu.trim() : null;
  const catId = Number(r.categoryId);
  return {
    id,
    title: String(r.title ?? ''),
    brand: r.brand != null ? String(r.brand) : null,
    category: r.category != null ? String(r.category) : null,
    categoryId: Number.isFinite(catId) ? catId : null,
    thumbnailUrl,
  };
}

function parseCondition(raw: unknown): ProductConditionOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  return { id, label: String(r.label ?? r.name ?? '') };
}

function parseSize(raw: unknown): ProductSizeOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  return { id, label: String(r.label ?? r.name ?? '') };
}

function parseCatalogOption(raw: unknown): AdminCatalogOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;
  const label = String(
    r.label ?? r.name ?? r.title ?? r.brand ?? r.category ?? r.model ?? ''
  ).trim();
  if (!label) return null;
  return { id, label };
}

export async function fetchAdminConsignors(): Promise<AdminConsignorOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/consignors');
    const items = unwrapItems<unknown>(data);
    return items.map(parseConsignor).filter((x): x is AdminConsignorOption => x !== null);
  } catch {
    return [];
  }
}

export async function searchAdminProducts(q: string): Promise<AdminProductSearchItem[]> {
  const query = q.trim();
  if (!query) return [];
  try {
    const { data } = await authClient.get<unknown>('/api/admin/products/search', {
      params: { q: query },
    });
    const items = unwrapItems<unknown>(data);
    return items.map(parseProduct).filter((x): x is AdminProductSearchItem => x !== null);
  } catch {
    return [];
  }
}

export async function fetchProductConditions(productId: number): Promise<ProductConditionOption[]> {
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/products/${productId}/conditions`);
    const items = unwrapItems<unknown>(data);
    return items.map(parseCondition).filter((x): x is ProductConditionOption => x !== null);
  } catch {
    return [];
  }
}

export async function fetchProductSizes(productId: number): Promise<ProductSizeOption[]> {
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/products/${productId}/sizes`);
    const items = unwrapItems<unknown>(data);
    return items.map(parseSize).filter((x): x is ProductSizeOption => x !== null);
  } catch {
    return [];
  }
}

export async function createProductSize(payload: {
  name: string;
  categoryId: number;
}): Promise<ProductSizeOption | null> {
  const trimmed = payload.name.trim();
  if (!trimmed || !Number.isFinite(payload.categoryId)) return null;
  try {
    const { data } = await authClient.post<unknown>('/api/admin/product-sizes', {
      name: trimmed,
      categoryId: payload.categoryId,
    });
    const item = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseSize(inner.item ?? inner);
    })();
    return item;
  } catch {
    return null;
  }
}

export async function createProductCondition(payload: {
  name: string;
  categoryId: number;
}): Promise<ProductConditionOption | null> {
  const trimmed = payload.name.trim();
  if (!trimmed || !Number.isFinite(payload.categoryId)) return null;
  try {
    const { data } = await authClient.post<unknown>('/api/admin/product-conditions', {
      name: trimmed,
      categoryId: payload.categoryId,
    });
    const item = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseCondition(inner.item ?? inner);
    })();
    return item;
  } catch {
    return null;
  }
}

export async function fetchProductBrands(): Promise<AdminCatalogOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/product-brands');
    const items = unwrapItems<unknown>(data);
    return items.map(parseCatalogOption).filter((x): x is AdminCatalogOption => x !== null);
  } catch {
    return [];
  }
}

export async function createProductBrand(name: string): Promise<AdminCatalogOption | null> {
  const trimmed = name.trim();
  if (!trimmed) return null;
  try {
    const { data } = await authClient.post<unknown>('/api/admin/product-brands', { name: trimmed });
    const item = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseCatalogOption(inner.item ?? inner);
    })();
    return item;
  } catch {
    return null;
  }
}

export async function fetchProductCategories(): Promise<AdminCatalogOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/product-categories');
    const items = unwrapItems<unknown>(data);
    return items.map(parseCatalogOption).filter((x): x is AdminCatalogOption => x !== null);
  } catch {
    return [];
  }
}

export async function createProductCategory(name: string): Promise<AdminCatalogOption | null> {
  const trimmed = name.trim();
  if (!trimmed) return null;
  try {
    const { data } = await authClient.post<unknown>('/api/admin/product-categories', {
      name: trimmed,
    });
    const item = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseCatalogOption(inner.item ?? inner);
    })();
    return item;
  } catch {
    return null;
  }
}

export async function searchProductModels(params: {
  q: string;
  brandId: number;
  categoryId: number;
}): Promise<AdminCatalogOption[]> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/product-models/search', {
      params: {
        q: params.q.trim() || undefined,
        brandId: params.brandId,
        categoryId: params.categoryId,
      },
    });
    const items = unwrapItems<unknown>(data);
    return items.map(parseCatalogOption).filter((x): x is AdminCatalogOption => x !== null);
  } catch {
    return [];
  }
}

export async function createProductModel(payload: {
  name: string;
  brandId: number;
  categoryId: number;
}): Promise<AdminCatalogOption | null> {
  const trimmed = payload.name.trim();
  if (!trimmed || !Number.isFinite(payload.brandId) || !Number.isFinite(payload.categoryId))
    return null;
  try {
    const { data } = await authClient.post<unknown>('/api/admin/product-models', {
      name: trimmed,
      brandId: payload.brandId,
      categoryId: payload.categoryId,
    });
    const item = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseCatalogOption(inner.item ?? inner);
    })();
    return item;
  } catch {
    return null;
  }
}

export interface CreateAdminProductPayload {
  brandId: number;
  categoryId: number;
  modelId: number;
  colorway: string;
  sku: string;
  description: string;
  image?: File | null;
}

export type CreateAdminProductResult =
  | { ok: true; product: AdminProductSearchItem }
  | { ok: false; error: string };

export async function createAdminProduct(
  payload: CreateAdminProductPayload
): Promise<CreateAdminProductResult> {
  try {
    const fd = new FormData();
    fd.append('brandId', String(payload.brandId));
    fd.append('categoryId', String(payload.categoryId));
    fd.append('modelId', String(payload.modelId));
    fd.append('colorway', payload.colorway.trim());
    fd.append('sku', payload.sku.trim().toUpperCase());
    fd.append('description', payload.description.trim());
    if (payload.image) {
      fd.append('image', payload.image);
    }
    const { data } = await authClient.post<unknown>('/api/admin/products', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const parsed = (() => {
      if (!data || typeof data !== 'object') return null;
      const o = data as Record<string, unknown>;
      const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
        string,
        unknown
      >;
      return parseProduct(inner.item ?? inner.product ?? inner);
    })();
    if (!parsed) {
      return { ok: false, error: 'El producto se creó, pero la respuesta no fue válida.' };
    }
    return { ok: true, product: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint para crear producto aún no está disponible.'
        : null) ??
      'No se pudo crear el producto.';
    return { ok: false, error: typeof msg === 'string' ? msg : 'No se pudo crear el producto.' };
  }
}

export interface InventoryBatchRowPayload {
  productId: number;
  sizeId: number;
  conditionId: number;
  cost: number;
  publicPrice: number;
  comments: string;
}

export interface InventoryBatchPayload {
  consignorId: number;
  rows: InventoryBatchRowPayload[];
}

export type SubmitBatchResult = { ok: true } | { ok: false; error: string };

export async function submitInventoryBatch(
  payload: InventoryBatchPayload
): Promise<SubmitBatchResult> {
  try {
    await authClient.post('/api/admin/inventory/batch', payload);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de envío aún no está disponible.' : null) ??
      'No se pudo registrar el lote.';
    return { ok: false, error: typeof msg === 'string' ? msg : 'No se pudo registrar el lote.' };
  }
}
